import { STORY_SOURCE, story } from '@/content/storyBuilder'

// The ninth written page. Everything below comes from the five-part full-page
// capture of digitopia.co/customers/akcansa/ sent on 2026-08-28 — "birebir tıpa
// tıp aynısını tasarla" — with the four photographs from the page's own media.
// Its shape is the reference's: no `intro` band, because the reference opens
// with two centred sections on the page ("About AkçanSA", "The Need") and only
// then lifts the four answering sections into panels. The closing quote is not
// a `section`: Aydın Şener's words have been in `content/testimonials.ts` keyed
// to this slug since the Hall of Fame shipped, Akçansa's mark and his portrait
// included, and `StoryBody` draws that card — the testimonial template the same
// request asked for at the foot of the page. His portrait was supplied again
// with this capture and the one already held is the better file (900×900 with a
// cut-out alpha channel, against an 880×880 WebP on white), so it stands.
export const added = story('akcansa', 'AkçanSA', {
  // The bucket key folds the ç, as Storage requires; the page keeps the name.
  doc: '7f7c1263-d81d-4d39-b901-4036fd305614-AkcanSA and Digitopia.pdf',
  // "Akçansa | Digitopia Success Stories", the film the reference's header
  // plays. Its id arrived on 2026-08-30, which is what the earlier check was
  // waiting for, so the header now holds the player rather than the mark.
  video: '6G0DdqcJc6U',
  lede: [
    'Unlike off-the-shelf solutions, Digitopia’s approach provided a structured framework to '
    + 'assess digital maturity, align leadership and teams on priorities, and create a clear '
    + 'roadmap for execution.',
  ],
  sections: [
    {
      heading: 'About AkçanSA',
      centred: true,
      body: [
        'AkçanSA, a joint venture between the international Heidelberg Cement Group and Sabanci '
        + 'Holding, is one of Turkey’s leading cement manufacturers. With multiple factories '
        + 'across Turkey, AkçanSA has established itself as a formidable player in the industry. '
        + 'Their commitment to quality, efficiency, and sustainability has been pivotal to their '
        + 'success in a highly competitive market.',
      ],
    },
    {
      heading: 'The Need: Driving Innovation in a Traditional Industry',
      centred: true,
      body: [
        'As Turkey’s leading cement producer, AkçanSA recognized the need to accelerate its '
        + 'digital transformation to remain competitive. AkçanSA faces continuous pressure to '
        + 'optimize efficiency, comply with local and global sustainability measures, and drive '
        + 'cost optimization. The intense competition and margin pressures necessitate a '
        + 'strategic approach to maintaining their market position while achieving operational '
        + 'excellence.',
        'Operating across multiple regions with integrated cement plants, terminals, and '
        + 'production facilities, the company sought to optimize operations, enhance efficiency, '
        + 'and develop new business models through technology. However, digital transformation '
        + 'in the cement industry is not just about technology; it requires a shift in culture, '
        + 'processes, and strategy.',
      ],
      photo: {
        src: '/photo/akcansa-logo-flowerbed-aerial.webp',
        alt: 'Mown lawns seen straight down from the air, with the AKÇANSA wordmark laid out in '
          + 'a rectangular flowerbed at the centre of them.',
      },
    },
    {
      heading: 'The Challenge: Beyond Standard Solutions',
      band: true,
      centred: true,
      body: [
        'While AkçanSA had been implementing advanced analytics and digital tools, the real '
        + 'challenge was fostering a company-wide transformation mindset. The cement industry '
        + 'traditionally operates on well-established methodologies, making it difficult to '
        + 'break away from conventional approaches. The goal was to integrate digital '
        + 'technologies into everyday processes, engage cross-functional teams, and align '
        + 'leadership with a shared vision for the future.',
      ],
      photo: {
        src: '/photo/akcansa-painted-mixer-truck.webp',
        alt: 'An AkçanSA concrete mixer truck parked on the quayside, its drum hand-painted in '
          + 'bright street-art lettering, with the 1915 Çanakkale bridge under construction '
          + 'behind it.',
      },
    },
    {
      heading: 'Why Digitopia: A Structured and Data-Driven Approach',
      band: true,
      centred: true,
      body: [
        'To achieve these objectives, AkçanSA partnered with Digitopia to leverage the Digital '
        + 'Maturity Index (DMI) solution and the Impact Platform. Unlike off-the-shelf '
        + 'solutions, Digitopia’s approach provided a structured framework to assess digital '
        + 'maturity, align leadership and teams on priorities, and create a clear roadmap for '
        + 'execution. The collaboration emphasized cross-functional engagement, bringing '
        + 'together HR, sales, IT, and operations to drive meaningful change.',
        'Since the beginning of their partnership, Digitopia has measured AkçanSA’s Digital '
        + 'Maturity Index (DMI) annually. This ongoing assessment has provided a structured '
        + 'approach to AkçanSA’s digital transformation journey. The DMI methodology has been '
        + 'instrumental in organizing and guiding their transformation efforts, offering clear '
        + 'insights into their digital capabilities and areas for improvement.',
      ],
    },
    {
      heading: 'The Approach: Innovation Through Experimentation',
      band: true,
      photo: {
        src: '/photo/akcansa-plant-at-dusk-digital.webp',
        alt: 'An AkçanSA cement plant lit up at dusk against a blue sky, with a rising grid of '
          + 'pale squares drawn over it.',
      },
      photoSide: 'left',
      body: [
        'AkçanSA adopted an agile, innovation-driven approach, allowing teams to experiment '
        + 'with new technologies and business models. The company launched an internal '
        + 'innovation program, encouraging employees to develop digital solutions, prototype '
        + 'ideas, and validate business cases before full-scale implementation. With '
        + 'Digitopia’s guidance, AkçanSA established:',
      ],
      items: [
        { text: 'A dedicated digital transformation team to drive change initiatives.' },
        {
          text: 'Agile project teams integrating business and technical roles for hands-on '
            + 'innovation.',
        },
        {
          text: 'A structured process for evaluating digital projects and ensuring alignment '
            + 'with business goals.',
        },
      ],
    },
    {
      heading: 'The Impact: A Future-Ready Cement Leader',
      band: true,
      photo: {
        src: '/photo/akcansa-plant-and-park.webp',
        alt: 'An AkçanSA cement works behind a landscaped park of lawns, young trees and a pond '
          + 'with a wooden gazebo beside it, under a bright blue sky.',
      },
      photoSide: 'right',
      photoInPanel: true,
      body: [
        'By leveraging the DMI assessments and the transformation platform, AkçanSA has '
        + 'achieved remarkable efficiency in their operations. The structured guidance provided '
        + 'by Digitopia has facilitated better organization and execution of digital '
        + 'initiatives, leading to optimized processes and reduced costs. Moreover, the focus '
        + 'on sustainability has allowed AkçanSA to comply with both local and global '
        + 'sustainability measures, reinforcing their commitment to environmental '
        + 'responsibility. AkçanSA has positioned itself as a forward-thinking leader in the '
        + 'cement industry. The company’s collaboration with Digitopia has enabled:',
      ],
      items: [
        {
          text: 'A structured roadmap for digital transformation, ensuring alignment across '
            + 'departments.',
        },
        { text: 'Increased efficiency through AI-driven analytics and process optimization.' },
        { text: 'Greater cross-functional collaboration, breaking down silos between teams.' },
        {
          text: 'A culture of innovation, empowering employees to actively contribute to '
            + 'digital initiatives.',
        },
      ],
    },
  ],
  check:
    'This page’s words were transcribed from a five-part full-page capture of '
    + `${STORY_SOURCE}/akcansa/ supplied on 2026-08-28, not from a file we hold, so please `
    + 'proofread it against the source before production — in particular the company’s two '
    + 'owners, which the reference writes as “Heidelberg Cement Group” and “Sabanci Holding” '
    + 'without its Turkish letters, where the rest of this site writes Sabancı. The spelling of '
    + 'the company itself is kept exactly as the reference sets it, “AkçanSA”, which is this '
    + 'catalogue’s own name for it and differs from the “Akçansa” on the logo wall and under '
    + 'the closing quote — worth settling in one place rather than here. Two deliberate '
    + 'differences from the reference, each of them a template-wide decision rather than this '
    + 'page’s: the header eyebrow reads “Digitopia / Customer Success Story” where digitopia.co '
    + 'reads “SUCCESS STORIES”; and the quote card at the foot is the Hall of Fame template as '
    + 'asked for, so its eyebrow is the company where the reference sets it as a rule under the '
    + 'word AKÇANSA — Mr Şener’s name and title are on the line under the quote either way. '
    + 'Two things to look at rather than '
    + 'decide. The left half of the reference’s Approach panel and the right half of its Impact '
    + 'panel are both blank in the capture, and the plant at dusk and the plant behind its park '
    + 'are placed in them on the assumption that they are what did not load; move or swap them '
    + 'if that is wrong. And the painted mixer truck under “The Challenge” is the one supplied '
    + 'photograph the reference gives no place at all — it is put there because that section is '
    + 'about breaking with convention, which is a reading rather than a copy, so drop it or '
    + 'move it and nothing else changes. Lastly, all four originals are small — 600–1200px, '
    + 'which is what the live site serves — so they are drawn at that width and will look soft '
    + 'on a high-density screen until larger files arrive.',
})

/** Its place in the hand-placed order the imported Customers page set. */
export const order = 10
